import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { PanelMenu } from 'primereact/panelmenu';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Ripple } from 'primereact/ripple';
import { Badge } from 'primereact/badge';
import { DataView, DataViewLayoutOptions } from 'primereact/dataview';
import { Message } from 'primereact/message';
import { Rating } from 'primereact/rating';
import '../styles/LazyLoading.css';
import { Tag } from 'primereact/tag';
import { Toolbar } from 'primereact/toolbar';
import { Divider } from 'primereact/divider';
import { classNames } from 'primereact/utils';
import { formatoImporte, porcentajeDescuentoEntero2Precios } from '../lib/formatos';
import { createProductUrl } from '../utils/urlUtils';
import { buildImageUrl } from '../lib/fileServer';
import CatalogFilters from '../components/catalog/CatalogFilters';
import CatalogSkeleton from '../components/catalog/CatalogSkeleton';
import { useCartActions } from '../hooks/useCartActions';
import { useTheme } from '../hooks/useTheme';
import { useCatalogContext } from '../contexts/CatalogContext';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { Paginator } from 'primereact/paginator';
import { Sidebar } from 'primereact/sidebar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Skeleton } from 'primereact/skeleton';
import { Card } from 'primereact/card';
import { Chip } from 'primereact/chip';
import { ScrollPanel } from 'primereact/scrollpanel';
import CatalogDebug from '../components/debug/CatalogDebug';




function CatalogoContent() {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const { addToCart } = useCartActions();
    const { theme, toggleTheme } = useTheme();

    const {
        equiposAMostrar,
        loading,
        error,
        pagination,
        sortCriteria,
        searchTerm,
        filters,
        filterData,
        filterLoading,
        totalRecords,
        first,
        rows,
        sortField,
        sortOrder,
        updateFilter,
        clearAllFilters,
        updateSearch,
        onPageChange,
        onSortChange,
        loadMore,
        refresh
    } = useCatalogContext();



    // Estado para lazy loading
    const [loadingMore, setLoadingMore] = useState(false);

    // Filtrar productos por rango de precios en el frontend como validación adicional
    const productosConFiltroPrecios = useMemo(() => {
        if (!equiposAMostrar || equiposAMostrar.length === 0) {
            return [];
        }

        // Mostrar todos los productos ya que el filtrado se hace en el backend
        return equiposAMostrar;
    }, [equiposAMostrar]);

    // Función para cargar más productos del servidor
    const loadMoreProducts = useCallback(() => {
        if (loadingMore || loading || productosConFiltroPrecios.length >= totalRecords) {
            return;
        }

        setLoadingMore(true);
        loadMore();

        // Desactivar loadingMore después de un tiempo
        setTimeout(() => {
            setLoadingMore(false);
        }, 1000);
    }, [loadingMore, loading, productosConFiltroPrecios.length, totalRecords, loadMore]);

    // Función para manejar el scroll infinito
    const handleScroll = useCallback(() => {
        if (loadingMore || loading || productosConFiltroPrecios.length >= totalRecords) {
            return;
        }

        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;

        // Cargar más cuando esté cerca del final (200px antes)
        if (scrollTop + windowHeight >= documentHeight - 200) {
            loadMoreProducts();
        }
    }, [loadingMore, loading, productosConFiltroPrecios.length, totalRecords, loadMoreProducts]);

    // Hook para detectar scroll
    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);


    // Función para obtener partes del precio
    const precioParteEntera = (precio) => {
        return formatoImporte(precio).split('.')[0];
    }

    const precioParteDecimal = (precio) => {
        return formatoImporte(precio).split('.')[1];
    }

    const [layout, setLayout] = useState('grid');
    const [isMobile, setIsMobile] = useState(false);
    const [showMobileFilters, setShowMobileFilters] = useState(false);

    // Combine loading states
    const isLoading = loading;

    // Detectar dispositivos móviles
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 1024);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Título de la pestaña para Catálogo
    useEffect(() => {
        document.title = 'Catálogo - Laptops.pe';
    }, []);

    // Opciones de ordenamiento - ahora manejadas por el hook
    const sortOptions = [
        { label: 'Precio: Menor a Mayor', value: 'precioventa_1' },
        { label: 'Precio: Mayor a Menor', value: 'precioventa_-1' },
        { label: 'Nombre: A-Z', value: 'nombre_1' },
        { label: 'Nombre: Z-A', value: 'nombre_-1' },
        { label: 'Más Populares', value: 'megusta_-1' },
        { label: 'Más Recientes', value: 'fecha_-1' }
    ];



    // Establecer criterio predeterminado al cargar
    useEffect(() => {
        if (!sortField || sortField === '') {
            onSortChange({ field: 'precioventa', order: 1 });
        }
    }, [sortField, onSortChange]);

    // Los productos ya vienen filtrados y paginados del hook

    // Función para toggle del sidebar móvil
    const toggleMobileFilters = () => {
        setShowMobileFilters(!showMobileFilters);
    };

    const catalogoLista = (producto, index) => {
        return (
            <div className='col-12 px-3' key={producto.id}>
                <div className={classNames('flex flex-column xl:flex-row xl:align-items-start', { 'border-top-1 surface-border': index !== 0 })}>
                    <div className="flex align-items-center surface-card w-full">
                        {/* Imagen del producto - Lado izquierdo */}
                        <div className="relative bg-white border-round shadow-1 my-1" style={{ width: '180px', height: '130px', minWidth: '180px' }}>
                            <a href={createProductUrl(producto.id, producto.nombre)} alt={producto.nombre}>
                                <img
                                    src={buildImageUrl(producto.foto_url || producto.imagen_portada || producto.imagen_principal)}
                                    alt={producto.nombre}
                                    style={{ objectFit: 'contain', width: '100%', height: '100%' }}
                                    className="border-round"
                                    onError={(e) => { e.currentTarget.src = '/productos/placeholder.png'; }}
                                />
                            </a>
                            {Number(producto.precio_venta) < Number(producto.precio_lista) && (
                                <>
                                    <Badge value={`-${porcentajeDescuentoEntero2Precios(producto.precio_lista, producto.precio_venta)}%`} severity="danger" className='absolute font-bold' style={{ top: '36px', left: '4px', backgroundColor: '#a50d00', color: '#fff' }}></Badge>
                                    <img src={`/logos/en-oferta.png`} alt="En oferta" height={40} className="absolute" style={{ top: '4px', left: '4px' }} />
                                </>
                            )}
                            {/* <Button type="text" className="p-ripple p-link w-2rem h-2rem hover:surface-200 bg-transparent border-transparent border-circle hover:shadow-2 inline-flex align-items-center justify-content-center absolute transition-colors transition-duration-300" style={{ top: '4px', right: '4px' }}>
                                <i className="pi pi-heart text-lg text-red-500"></i>
                                <Ripple />
                            </Button>
                            {producto.megustas > 0 && (
                                <div className="absolute text-center w-2rem" style={{ top: '28px', right: '4px' }}>
                                    <span className='text-red-500 font-bold bg-white border-round px-1' style={{ fontSize: '12px' }}>{producto.megustas}</span>
                                </div>
                            )}
                            <div className="absolute text-center w-1rem" style={{ bottom: '2px', left: '4px' }}>
                                <img src={producto.marca_logo || producto.marcalogo} height={20} alt="Marca" />
                            </div> */}
                        </div>

                        {/* Información del producto - Centro */}
                        <div className="flex-1 ml-3">
                            {/* Nombre del producto */}
                            <a href={createProductUrl(producto.id, producto.nombre)} className='flex-initial text-primary font-bold text-sm lg:text-lg my-2 hover:text-cyan-500 no-underline'>{producto.nombre}</a>

                            {/* Logos de marca y especificaciones */}
                            {/* <div className='hidden lg:block'>
                                <div className="flex gap-2">                                
                                    <img src={producto.procesadorlogo} height={50} className='flex-initial flex align-items-center justify-content-center' alt="Procesador" />
                                    <img src={producto.tarjetagraficalogo} height={50} className='flex-initial flex align-items-center justify-content-center' alt="Tarjeta Gráfica" />
                                    <div className="flex-initial flex align-items-center justify-content-center" style={{ backgroundColor: producto.colorhex, width: '50px', height: '50px' }}></div>
                                </div>
                            </div> */}

                            {/* Precios */}
                            <div className="flex align-items-center gap-3 mt-2">
                                {Number(producto.precio_venta) < Number(producto.precio_lista) && (
                                    <div className="flex gap-3">
                                        <div className="flex-initial flex text-sm text-500">Precio: S./ {formatoImporte(producto.precio_lista)}</div>
                                        <div className="flex-initial flex text-sm font-bold text-red-500">Ahorra: S./ {formatoImporte(Number(producto.precio_lista) - Number(producto.precio_venta))}</div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Botones de acción - Lado derecho */}
                        <div className="flex flex-column gap-2" style={{ minWidth: '140px' }}>
                            {/* COMENTADO SEGÚN REQUERIMIENTO PARA PIVOT A WHATSAPP */}
                            {/* <Button
                                label="Comprar Ahora"
                                onClick={() => addToCart(producto, 1)}
                                icon="pi pi-shopping-cart"
                                className="w-full p-button-sm"
                                severity="success"
                            /> */}
                            
                            {/* NUEVO: Botón de WhatsApp para Vista Lista */}
                            <Button
                                label="Comprar por WhatsApp"
                                onClick={() => {
                                    const numeroWhatsApp = '51978326796';
                                    const urlProducto = `${window.location.origin}${createProductUrl(producto.id, producto.nombre)}`;
                                    const mensaje = 
`¡Hola Laptops.pe! 🖥️ Me interesa este producto del catálogo:

🛒 *Producto:* ${producto.nombre}
💵 *Precio:* S/ ${Number(producto.precio_venta).toFixed(2)}
🔗 *Enlace:* ${urlProducto}

¿Tienen stock disponible para coordinar la compra? ¡Gracias!`;
                                    window.open(`https://wa.me/${numeroWhatsApp}?text=${encodeURIComponent(mensaje)}`, '_blank', 'noopener,noreferrer');
                                }}
                                icon="pi pi-whatsapp"
                                className="w-full p-button-sm text-white font-bold"
                                style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
                            />
                            <div className="text-cyan-500 font-bold text-right text-5xl">
                                <span className='text-sm'>S./</span>{precioParteEntera(producto.precio_venta)}<span className='text-sm'>.{precioParteDecimal(producto.precio_venta)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const catalogoGrid = (producto) => {
        return (
            <div className='col-12 sm:col-6 lg:col-4 xl:col-3' key={producto.id}>
                <div className="p-1 lg:p-2 lg:m-2 border-1 surface-border">
                    <div className="relative bg-white" style={{ height: '280px', maxHeight: '280px' }}>
                        <a href={createProductUrl(producto.id, producto.nombre)} alt={producto.nombre}>
                            <img
                                src={`${process.env.REACT_APP_FILE_SERVER_URL}${producto.foto_url}`}
                                alt={producto.nombre}
                                style={{ objectFit: 'contain', width: '100%', height: '100%' }}
                                className="w-full"
                            />
                        </a>
                        {/* <Button type="text" className="p-ripple p-link w-3rem h-3rem hover:surface-200 bg-transparent border-circle border- hover:shadow-2 inline-flex align-items-center justify-content-center absolute transition-colors transition-duration-300" style={{ top: '2px', right: '2px' }}>
                            <i className="pi pi-heart text-2xl text-red-500"></i>
                            <Ripple />
                        </Button>
                        <div className="flex justify-content-center flex-wrap absolute w-3rem" style={{ top: '2.5rem', right: '2px' }}>
                            {producto.megustas > 0 && <span className='text-red-500 font-bold' style={{ fontSize: '12px', textShadow: '1px 1px 0 #FFFFFF' }}>{producto.megustas}</span>}
                        </div> */}
                        {Number(producto.precio_venta) < Number(producto.precio_lista) && <img src={`/logos/en-oferta.png`} alt="En oferta" height={72} className="absolute" style={{ top: '72px', right: '2px' }} />}
                        {/* {Number(producto.precio_venta) < Number(producto.precio_lista) && <img src={`/navidadlogo.png`} alt="En oferta" height={80} className="absolute" style={{ top: '92px', right: '2px' }} />}
                        {Number(producto.precio_venta) < Number(producto.precio_lista) && <Badge value={`-${porcentajeDescuentoEntero2Precios(producto.precio_lista, producto.precio_venta)}%`} severity="danger" className='absolute' size="small" style={{ top: '95px', right: '2px', backgroundColor: 'transparent', color: '#ff0000ff' }}></Badge>} */}
                        {Number(producto.precio_venta) < Number(producto.precio_lista) && <Badge value={`Dcto.${porcentajeDescuentoEntero2Precios(producto.precio_lista, producto.precio_venta)}%`} severity="danger" className='absolute' style={{ top: '130px', right: '2px', backgroundColor: '#a50d00', color: '#fff' }}></Badge>}
                        {/* <img src={producto.marca_logo || producto.marcalogo} height={25} className="absolute" style={{ top: '2px', left: '2px' }} alt="Marca" />
                        <img src={producto.configuracion?.procesadorlogo || producto.procesadorlogo} height={64} className="absolute" style={{ bottom: '2px', left: '2px' }} alt="Procesador" />
                        <img src={producto.configuracion?.tarjetagraficalogo || producto.tarjetagraficalogo} height={64} className="absolute" style={{ bottom: '70px', left: '2px' }} alt="Tarjeta Gráfica" />
                        <div className="flex justify-content-center flex-wrap absolute" style={{ bottom: '2px', left: '68px', height: '26px', width: '26px', backgroundColor: producto.configuracion?.colorhex || producto.colorhex }}></div>
                        <div className="flex justify-content-center flex-wrap absolute" style={{ bottom: '2px', right: '2px', height: '44px', width: '44px' }}>
                            <img src={`/logos/ssd.png`} height={26} alt="SSD" /><br />
                            <span className="font-bold text-cyan-700" style={{ fontSize: '10px' }}>{producto.configuracion?.almacenamiento || producto.almacenamiento}</span>
                            <Ripple />
                        </div>
                        <div className="flex justify-content-center flex-wrap absolute" style={{ bottom: '48px', right: '2px', height: '44px', width: '44px' }}>
                            <img src={`/logos/memory-ram.png`} height={26} alt="RAM" /><br />
                            <span className="font-bold text-cyan-700" style={{ fontSize: '10px' }}>{producto.configuracion?.memoria || producto.memoria}</span>
                            <Ripple />
                        </div> */}
                    </div>
                    <div className="flex align-items-center justify-content-between mt-2 mb-2">
                        <a href={createProductUrl(producto.id, producto.nombre)} className='flex-initial text-cyan-500 font-bold text-sm lg:text-md my-2 hover:text-blue-500 no-underline'>{producto.nombre}</a>
                    </div>
                    <div className='flex align-items-center justify-content-between mt-2 mb-2'>
                        {Number(producto.precio_venta) < Number(producto.precio_lista) && (
                            <div className="mr-auto">
                                <span className=" text-xs  p-overlay-badge w-full">Precio: S./ {formatoImporte(producto.precio_lista)}</span><br />
                                <span className="font-bold text-xs text-red-500">Ahorra: S./ {formatoImporte(Number(producto.precio_lista) - Number(producto.precio_venta))}</span>
                            </div>
                        )}
                        <div className="text-cyan-500 font-bold text-2xl lg:text-4xl text-right ml-auto"><span className='text-xs lg:text-sm'>S./</span> {precioParteEntera(producto.precio_venta)}<span className='text-xs lg:text-sm'>.{precioParteDecimal(producto.precio_venta)}</span></div>
                    </div>
                    <div className='flex align-items-center justify-content-between gap-2'>
                        {/* <Button label="Ver Detalles..." onClick={() => navigate(createProductUrl(producto.id, producto.nombre))} outlined className='flex-initial flex justify-content-center align-items-center' /> */}
                        <a href={`${process.env.REACT_APP_SITE_URL}/catalogo?canal=${encodeURIComponent(String(producto.canal || '').toLowerCase().replace(/\s+/g, '-'))}`} className="no-underline">
                            {producto.canal || 'Canal'}
                        </a>
                        {/* COMENTADO SEGÚN REQUERIMIENTO PARA PIVOT A WHATSAPP */}
                        {/* <Button label="Comprar" onClick={() => addToCart(producto, 1)} className='flex-initial flex justify-content-center align-items-center' severity="primary">
                            <i className="pi pi-shopping-cart ml-2"></i>
                        </Button> */}

                        {/* NUEVO: Botón de WhatsApp para Vista Cuadrícula */}
                        <Button 
                            label="Comprar" 
                            onClick={() => {
                                const numeroWhatsApp = '51978326796';
                                const urlProducto = `${window.location.origin}${createProductUrl(producto.id, producto.nombre)}`;
                                const mensaje = 
`¡Hola Laptops.pe! 🖥️ Me interesa este producto del catálogo:

🛒 *Producto:* ${producto.nombre}
💵 *Precio:* S/ ${Number(producto.precio_venta).toFixed(2)}
🔗 *Enlace:* ${urlProducto}

¿Tienen stock disponible para coordinar la compra? ¡Gracias!`;
                                window.open(`https://wa.me/${numeroWhatsApp}?text=${encodeURIComponent(mensaje)}`, '_blank', 'noopener,noreferrer');
                            }} 
                            className='flex-initial flex justify-content-center align-items-center text-white font-bold' 
                            style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
                        >
                            <i className="pi pi-whatsapp ml-2"></i>
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    const itemTemplate = (producto, layout, index) => {
        if (!producto) {
            return;
        }

        if (layout === 'list') return catalogoLista(producto, index);
        else if (layout === 'grid') return catalogoGrid(producto);
    };

    const handleQuickSort = (field, order) => {
        onSortChange(field, order);
    };

    const clearSort = () => {
        onSortChange('precioventa', 1);
    };

    const header = () => {
        const handleSortChange = (event) => {
            const value = event.value;
            const [field, order] = value.split('_');
            onSortChange(field, parseInt(order));
        };

        return (
            <div className="p-1">
                {/* Información de resultados */}
                <div className="flex flex-column md:flex-row justify-content-between align-items-center mb-0">
                    <div className="text-primary font-semibold text-sm mb-0 md:mb-0">
                        {totalRecords || 0} {(totalRecords || 0) === 1 ? 'producto encontrado' : 'productos encontrados'}
                        {Object.values(filters).some(f => f !== null) && (
                            <span className="text-600 font-normal ml-2">
                                con filtros aplicados
                            </span>
                        )}
                    </div>

                    {/* Filtros rápidos */}
                    <div className="flex flex-column md:flex-row gap-2 w-full md:w-auto">
                        <div className="flex gap-2 w-full md:w-auto">
                            <Button
                                label="Más Populares"
                                icon="pi pi-star"
                                outlined
                                size="small"
                                className={`p-button-xs ${isMobile ? 'flex-1' : ''}`}
                                onClick={() => handleQuickSort('megusta', -1)}
                            />
                            <Button
                                label="Nuevos"
                                icon="pi pi-plus"
                                outlined
                                size="small"
                                className={`p-button-xs ${isMobile ? 'flex-1' : ''}`}
                                onClick={() => handleQuickSort('fecha', -1)}
                            />
                        </div>
                        <div className="flex gap-2 w-full md:w-auto">
                            <Dropdown
                                options={sortOptions}
                                value={`${sortField}_${sortOrder}`}
                                onChange={handleSortChange}
                                placeholder="Ordenar por"
                                className={`${isMobile ? 'flex-1' : 'w-14rem'}`}
                            />
                            <Button
                                icon="pi pi-refresh"
                                outlined
                                size="small"
                                tooltip="Limpiar ordenamiento"
                                onClick={clearSort}
                                className="p-button-xs"
                            />
                            {!isMobile && (
                                <DataViewLayoutOptions layout={layout} onChange={(e) => setLayout(e.value)} />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={`min-h-screen flex flex-column theme-${theme}`}>
            {/* Header del landing page */}
            <Header theme={theme} onToggleTheme={toggleTheme} />

            <div className="flex-1 flex">
                {/* Filtros Desktop */}
                <div className="surface-section h-full lg:h-auto hidden lg:block flex-shrink-0 absolute lg:static left-0 top-0 z-1 border-right-1 surface-border select-none" style={{ width: '320px' }}>
                    <div className="flex flex-column h-full sistema-fondo-activo">
                        <CatalogFilters
                            filters={filters}
                            onFilterChange={updateFilter}
                            filterData={filterData}
                            loadingStates={filterLoading}
                            onPriceChange={(min, max) => {
                                updateFilter('precioMin', min);
                                updateFilter('precioMax', max);
                            }}
                            clearFilters={clearAllFilters}
                        />
                    </div>
                </div>

                {/* Botón Flotante para Filtros Móviles */}
                {isMobile && (
                    <Button
                        icon="pi pi-filter"
                        className="p-button-rounded p-button-primary fixed"
                        style={{
                            bottom: '20px',
                            left: '20px',
                            zIndex: 1000,
                            width: '60px',
                            height: '60px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                        }}
                        onClick={toggleMobileFilters}
                        tooltip="Filtros"
                        tooltipOptions={{ position: 'top' }}
                    />
                )}

                <div className={`min-h-screen flex flex-column relative flex-auto w-full ${isMobile ? 'w-full' : ''}`}>
                    <div className='overflow-y-auto overflow-x-auto m-0 md:m-3 p-0'>
                        <div className='surface-card shadow-2 border-round sistema-fondo-activo'>
                            <div className="card">
                                {isLoading ? (
                                    <CatalogSkeleton />
                                ) : error ? (
                                    <Message
                                        severity="error"
                                        text={`Error al cargar productos: ${error}`}
                                        className="w-full"
                                    />
                                ) : productosConFiltroPrecios?.length > 0 ? (
                                    <div>
                                        {header()}
                                        <DataView
                                            value={productosConFiltroPrecios}
                                            layout={layout}
                                            itemTemplate={layout === 'list' ? catalogoLista : catalogoGrid}
                                            emptyMessage="No se encontraron productos que coincidan con tu búsqueda."
                                        />

                                        {/* Indicador de carga flotante */}
                                        {loadingMore && (
                                            <div className={`lazy-loading-indicator ${loadingMore ? 'visible' : ''}`}>
                                                <i className="pi pi-spin pi-spinner lazy-loading-spinner"></i>
                                                <p className="lazy-loading-text">Cargando...</p>
                                            </div>
                                        )}

                                        {/* Botón para cargar más si hay más productos disponibles */}
                                        {!loadingMore && productosConFiltroPrecios.length < totalRecords && (
                                            <div className="text-center p-4">
                                                <button
                                                    className="load-more-button"
                                                    onClick={loadMoreProducts}
                                                >
                                                    Cargar más productos ({totalRecords - productosConFiltroPrecios.length} restantes)
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="p-4 text-center">
                                        <div className="mb-3">
                                            <i className="pi pi-search text-6xl text-gray-400"></i>
                                        </div>
                                        <h3 className="text-xl font-semibold mb-2">No se encontraron productos</h3>
                                        <p className="text-600">Intenta ajustar los filtros o realizar una nueva búsqueda</p>
                                    </div>
                                )}

                                {/* Footer con información adicional */}
                                <div className="p-4 border-top-1 surface-border ">
                                    <div className="grid">
                                        <div className="col-12 md:col-4">
                                            <div className="flex align-items-center gap-2 mb-2">
                                                <i className="pi pi-shield text-green-500"></i>
                                                <span className="font-semibold">Garantía Extendida</span>
                                            </div>
                                            <p className="text-600 text-sm m-0">Las garantías están sujetas a las políticas del canal de ventas</p>
                                        </div>
                                        <div className="col-12 md:col-4">
                                            <div className="flex align-items-center gap-2 mb-2">
                                                <i className="pi pi-truck text-blue-500"></i>
                                                <span className="font-semibold">Envío Gratis</span>
                                            </div>
                                            <p className="text-600 text-sm m-0">Verifique las políticas de envío del canal de ventas</p>
                                        </div>
                                        <div className="col-12 md:col-4">
                                            <div className="flex align-items-center gap-2 mb-2">
                                                <i className="pi pi-headphones text-purple-500"></i>
                                                <span className="font-semibold">Soporte 24/7</span>
                                            </div>
                                            <p className="text-600 text-sm m-0">Atención técnica especializada todos los días</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer del landing page */}
            <Footer />

            {/* Sidebar de filtros para móviles */}
            {isMobile && (
                <Sidebar
                    visible={showMobileFilters}
                    onHide={() => setShowMobileFilters(false)}
                    position="left"
                    className="p-sidebar-sm"
                    style={{ width: '85vw', maxWidth: '360px' }}
                >
                    <div className="p-3">
                        <div className="flex justify-content-between align-items-center mb-2">
                            <span className="font-semibold">Filtros</span>
                            <Button icon="pi pi-times" rounded text onClick={() => setShowMobileFilters(false)} />
                        </div>
                        <CatalogFilters
                            filters={filters}
                            onFilterChange={updateFilter}
                            filterData={filterData}
                            loadingStates={filterLoading}
                            onPriceChange={(min, max) => {
                                updateFilter('precioMin', min);
                                updateFilter('precioMax', max);
                            }}
                            clearFilters={clearAllFilters}
                        />
                    </div>
                </Sidebar>
            )}

            {/* Debug component - solo en desarrollo */}
            {/* {process.env.NODE_ENV === 'development' && <CatalogDebug />} */}
        </div>
    )
}

export default function CatalogoPage() {
    return <CatalogoContent />;
}
